import { useEffect, useMemo, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { Flex, Modal, Progress, Spin, Typography, theme } from 'antd';
import { LuCheck, LuChevronRight, LuX } from 'react-icons/lu';

import { useObjectPickerStore } from '@stores/useObjectPickerStore';
import { isCollectionResultQueryFor } from '@utils/collectionQueries';
import { FAILED_JOB_STATUSES, JOB_STATUS, TERMINAL_JOB_STATUSES } from '@utils/constants';
import { useResumeDetectionJob } from '@hooks/useResumeDetectionJob';
import { useParams } from 'react-router';

const { Text, Title } = Typography;

const AUTO_CLOSE_DELAY_MS = 5000;
// Ceiling on the wait *after* that delay, so a slow or failing refetch can't pin
// the modal open over the map indefinitely. Worst case the modal lives for the
// sum of the two.
const MAX_RESULTS_WAIT_MS = 15000;

/**
 * Progress modal shown while a detection job runs. Non-dismissible during
 * execution so the user is aware of the running job; once the job reaches a
 * terminal state (finished/failed) the close button appears and the modal
 * auto-closes — after a short delay, and once the results it triggered have
 * loaded, so it hands over to a finished map rather than an empty one.
 */
const DetectionProgressModal = () => {
	const { token } = theme.useToken();
	const { project_uuid } = useParams();

	const activeJobProgressModal = useObjectPickerStore(s => s.activeJobProgressModal);
	const setActiveJobProgressModal = useObjectPickerStore(s => s.setActiveJobProgressModal);

	const { data, isError, jobCollectionUuid } = useResumeDetectionJob(project_uuid);

	const jobStatus = data?.job_status;
	const isFinished = jobStatus === JOB_STATUS.FINISHED;
	const isFailed = FAILED_JOB_STATUSES.includes(jobStatus);
	const isTerminal = TERMINAL_JOB_STATUSES.includes(jobStatus);
	const messages = data?.status_messages ?? [];
	const latest = messages[messages.length - 1];
	const percent = isFinished ? 100 : (latest?.progress ?? 0);
	const collectionName = data?.job_meta?.collection?.name || '';

	// A finished job selects its collection and refreshes the result queries (see
	// useResumeDetectionJob). Tracking the fetches for *that* collection lets the
	// modal stay up until the results are actually on the map, without unrelated
	// collection traffic reading as "still loading".
	const resultsPredicate = useMemo(
		() => (jobCollectionUuid ? isCollectionResultQueryFor(jobCollectionUuid) : () => false),
		[jobCollectionUuid]
	);
	const isFetchingResults = useIsFetching({ predicate: resultsPredicate }) > 0;
	const [closeDelayElapsed, setCloseDelayElapsed] = useState(false);

	// Hold the modal for a few seconds once the job reaches a terminal state, so
	// the user sees the final result before it disappears.
	useEffect(() => {
		if (!isTerminal || !activeJobProgressModal) {
			// Re-arm for the next job.
			setCloseDelayElapsed(false);
			return;
		}

		const delay = setTimeout(() => setCloseDelayElapsed(true), AUTO_CLOSE_DELAY_MS);
		return () => clearTimeout(delay);
	}, [isTerminal, activeJobProgressModal]);

	// Then wait out the results refetch. Closing on the timer alone swaps the
	// completed run for an empty map or a "Loading detections" pill — the delay is
	// long enough for the refetch to have registered, so a quiet cache here means
	// the results have landed (a failed run refreshes nothing and closes on time).
	// Re-checking the job state matters: a new run can start while this is still
	// waiting, and closing on the previous job's stale flag would hide its card.
	useEffect(() => {
		if (!isTerminal || !activeJobProgressModal || !closeDelayElapsed) return;

		if (!isFetchingResults) {
			setActiveJobProgressModal(false);
			return;
		}

		const failsafe = setTimeout(() => setActiveJobProgressModal(false), MAX_RESULTS_WAIT_MS);
		return () => clearTimeout(failsafe);
	}, [
		isTerminal,
		activeJobProgressModal,
		closeDelayElapsed,
		isFetchingResults,
		setActiveJobProgressModal,
	]);

	let title = `Detecting for collection ${collectionName}`;
	if (isFinished) title = `Detection complete for ${collectionName}`;
	else if (isFailed) title = `Detection failed for ${collectionName}`;

	const rowColor = (done, failedRow) => {
		if (failedRow) return token.colorError;
		if (done) return token.colorSuccess;
		return token.colorTextSecondary;
	};

	return (
		<Modal
			open={activeJobProgressModal}
			size="small"
			// Below the navigation header (z-index: 100) so the modal's mask
			// doesn't block nav links — users can navigate away during a job.
			zIndex={99}
			style={{
				top: 'calc(100vh - 248px)',
				width: 440,
				maxWidth: 'calc(100vw - 48px)',
			}}
			footer={null}
			closable={isTerminal}
			mask={{ closable: isTerminal }}
			keyboard={isTerminal}
			onCancel={() => setActiveJobProgressModal(false)}>
			<Flex vertical gap={12}>
				<Flex justify="space-between" gap={12}>
					<Flex align="center" gap={10}>
						{isFinished ? (
							<LuCheck size={18} color={token.colorSuccess} />
						) : isFailed ? (
							<LuX size={18} color={token.colorError} />
						) : (
							<Spin size="small" />
						)}
						<Title level={5} style={{ margin: 0 }}>
							{title}
						</Title>
					</Flex>
					<Flex gap={8} style={{ width: 80 }}>
						<Text
							strong
							style={{
								color: isFailed ? token.colorError : token.colorSuccess,
							}}>
							{percent}%
						</Text>
					</Flex>
				</Flex>

				<Progress
					percent={percent}
					showInfo={false}
					status={isFailed ? 'exception' : isFinished ? 'success' : 'active'}
					strokeColor={token.colorSuccess}
				/>

				{isError ? (
					<Text type="danger">Couldn't load detection progress.</Text>
				) : messages.length === 0 ? (
					<Text type="secondary">Starting detection…</Text>
				) : (
					<Flex vertical gap={6} style={{ maxHeight: 200, overflowY: 'auto' }}>
						{messages.map((msg, index) => {
							const isLast = index === messages.length - 1;
							const done = isFinished || !isLast;
							const failedRow = isFailed && isLast;
							return (
								<Flex key={`${msg.timestamp}-${index}`} align="center" gap={8}>
									{failedRow ? (
										<LuX size={14} color={token.colorError} />
									) : done ? (
										<LuCheck size={14} color={token.colorSuccess} />
									) : (
										<LuChevronRight
											size={14}
											color={token.colorTextSecondary}
										/>
									)}
									<Text
										style={{
											color: rowColor(done, failedRow),
											fontSize: 13,
										}}>
										{msg.text}
									</Text>
								</Flex>
							);
						})}
					</Flex>
				)}
			</Flex>
		</Modal>
	);
};

export default DetectionProgressModal;
